import { useEffect, useRef, useState } from 'react';
import { Lock, X } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useCurrencyDisplayStore, type DisplayCurrency } from '../store/currency-display-store';
import { cn } from '@/lib/utils';

/** localStorage key — flipped to '1' on first user toggle, never shown again. */
const HINT_STORAGE_KEY = 'blu-373-currency-toggle-hint-seen';

/**
 * BLU-373 PR2b: segmented pill on the PO page that toggles every Money
 * cell between PO currency and org base currency.
 *
 * Hidden when po.currency === org.default_currency (toggle would be a
 * no-op — better to not surface a control that does nothing).
 *
 * Below the pill: rate line. When the order is paid, shows a Lock icon +
 * the lock date if available (PR2c will provide order.paid_at).
 */
interface CurrencyTogglePillProps {
  poCurrency: string | null | undefined;
  exchangeRate: number | null | undefined;
  /** Display Lock + locked date if true. */
  isLocked?: boolean;
  /** Optional ISO timestamp when the rate was locked. Renders "Locked Apr 23, 2026" if provided. */
  lockedAt?: string | null;
  /**
   * When true, the rate line and first-time hint render ABOVE the pill (and
   * the column aligns to the start). Use inside the floating action bar so
   * the pill itself sits flush with the bar's bottom edge alongside other
   * pills/buttons. Default false (legacy: rate below pill, right-aligned).
   */
  rateAbove?: boolean;
  className?: string;
}

const formatLockDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return 'locked';
  }
};

export const CurrencyTogglePill = ({
  poCurrency,
  exchangeRate,
  isLocked = false,
  lockedAt = null,
  rateAbove = false,
  className,
}: CurrencyTogglePillProps) => {
  const { organization } = useOrganization();
  const orgCurrency = organization?.default_currency ?? 'AUD';
  const displayCurrency = useCurrencyDisplayStore((s) => s.displayCurrency);
  const setDisplayCurrency = useCurrencyDisplayStore((s) => s.setDisplayCurrency);

  // BLU-373 PR2b polish: first-time hint. Show until the user has toggled
  // (any path — pill click or Shift+C). Persisted across sessions via
  // localStorage so it never re-appears.
  const [showHint, setShowHint] = useState(false);
  useEffect(() => {
    try {
      if (!localStorage.getItem(HINT_STORAGE_KEY)) {
        setShowHint(true);
      }
    } catch {
      // localStorage unavailable (e.g. private mode); just don't show the hint
    }
  }, []);

  const dismissHint = () => {
    setShowHint(false);
    try {
      localStorage.setItem(HINT_STORAGE_KEY, '1');
    } catch {
      // ignore — UI state already updated
    }
  };

  const handleToggle = (value: DisplayCurrency) => {
    setDisplayCurrency(value);
    if (showHint) dismissHint();
  };

  // Catch hotkey-driven toggles too (Shift+C bypasses the pill onClick).
  // Skip the very first render so the hint isn't dismissed on mount.
  const initialDisplayRef = useRef(displayCurrency);
  useEffect(() => {
    if (displayCurrency !== initialDisplayRef.current && showHint) {
      dismissHint();
    }
  }, [displayCurrency, showHint]);

  const effectivePoCurrency = poCurrency ?? orgCurrency;

  // Same currency = no toggle to show
  if (effectivePoCurrency === orgCurrency) {
    return null;
  }

  const rate = exchangeRate ?? 1;
  const formattedRate = rate.toFixed(4);
  const lockLabel = isLocked
    ? lockedAt
      ? `Locked ${formatLockDate(lockedAt)}`
      : 'Locked'
    : null;

  return (
    <div
      className={cn(
        'inline-flex gap-1',
        rateAbove ? 'flex-col-reverse items-start' : 'flex-col items-end',
        className,
      )}
      // Announce the keyboard shortcut to assistive tech
      aria-keyshortcuts="Shift+C"
    >
      <Tabs
        value={displayCurrency}
        onValueChange={(value) => handleToggle(value as DisplayCurrency)}
      >
        {/* Brand-coloured active state: threadheads-purple matches the
            primary CTA pattern (CSV wizard Apply, StepIndicator, etc.) so
            the toggle reads as a first-class control rather than a
            chrome-y secondary affordance. */}
        <TabsList className="h-8" title="Toggle currency display (Shift+C)">
          <TabsTrigger
            value="org"
            className="h-6 px-3 text-[11px] font-mono uppercase tracking-wide data-[state=active]:bg-threadheads-purple data-[state=active]:text-white data-[state=active]:shadow-sm"
            aria-label={`Show in ${orgCurrency}`}
          >
            {orgCurrency}
          </TabsTrigger>
          <TabsTrigger
            value="po"
            className="h-6 px-3 text-[11px] font-mono uppercase tracking-wide data-[state=active]:bg-threadheads-purple data-[state=active]:text-white data-[state=active]:shadow-sm"
            aria-label={`Show in ${effectivePoCurrency}`}
          >
            {effectivePoCurrency}
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
        <span className="font-mono tabular-nums">
          1 {effectivePoCurrency} = {formattedRate} {orgCurrency}
        </span>
        {lockLabel && (
          <span className="inline-flex items-center gap-0.5 text-muted-foreground">
            <Lock className="h-3 w-3" aria-hidden="true" />
            <span>{lockLabel}</span>
          </span>
        )}
      </div>
      {/* BLU-373 PR2b polish: first-time hint, dismisses on first toggle. */}
      {showHint && (
        <div
          className="inline-flex items-center gap-1 rounded-md border border-threadheads-purple/40 bg-threadheads-purple/10 px-2 py-0.5 text-[10px] text-threadheads-purple"
          role="status"
        >
          <span>
            New: tap <kbd className="rounded border border-threadheads-purple/30 bg-white px-1 font-mono text-[9px]">Shift+C</kbd> to flip currencies
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-4 w-4 p-0 text-threadheads-purple hover:bg-threadheads-purple/20"
            onClick={dismissHint}
            aria-label="Dismiss currency toggle hint"
          >
            <X className="h-3 w-3" aria-hidden="true" />
          </Button>
        </div>
      )}
    </div>
  );
};
